import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createBlock as _createBlock, renderList as _renderList, withModifiers as _withModifiers, Transition as _Transition, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "h-6 w-full skeleton-loading"
}
const _hoisted_2 = { class: "flex flex-col" }
const _hoisted_3 = {
  key: 0,
  class: "text-md font-medium"
}
const _hoisted_4 = {
  key: 1,
  class: "text-md font-medium"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm font-light text-gray-500" }, " Carriers require that all VolP numbers be registered before they can send text messages to US numbers. ", -1)
const _hoisted_6 = { class: "flex justify-between items-center mb-4" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("h3", { class: "ui-card__title" }, " Voice and SMS phone numbers "),
  /*#__PURE__*/_createElementVNode("p", { class: "ui-card__subtitle" }, " Manage voice and sms phone numbers in your company ")
], -1)
const _hoisted_8 = {
  key: 0,
  class: "container-px relative min-h-300 flex justify-center items-center"
}
const _hoisted_9 = { class: "flex flex-col" }
const _hoisted_10 = { class: "text-xs font-medium text-dark-blue-500 opacity-50" }
const _hoisted_11 = { class: "group-hover:opacity-100 group-focus-within:opacity-100 group-active:opacity-100 group-focus:opacity-100 opacity-0 pr-2 flex" }
const _hoisted_12 = {
  key: 1,
  class: "text-center my-12"
}
const _hoisted_13 = /*#__PURE__*/_createElementVNode("p", { class: "text-xl" }, " You have no phone numbers! ", -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, " Add at least one to start using telephony ", -1)
const _hoisted_15 = { key: 1 }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("div", { class: "skeleton-loading h-5 mb-1 w-48" }, null, -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("div", { class: "skeleton-loading h-3 w-52" }, null, -1)
const _hoisted_18 = [
  _hoisted_16,
  _hoisted_17
]
const _hoisted_19 = {
  key: 1,
  class: "text-center mt-12"
}
const _hoisted_20 = /*#__PURE__*/_createElementVNode("p", { class: "text-xl" }, " You have voice and sms disabled. ", -1)
const _hoisted_21 = {
  key: 0,
  class: "absolute bottom-0 left-4 right-4 rounded-md py-1 mb-2 bg-dark-blue-400 bg-opacity-10 pl-3 text-sm justify-center mt-2 flex"
}
const _hoisted_22 = {
  key: 1,
  class: "text-center my-12"
}
const _hoisted_23 = /*#__PURE__*/_createElementVNode("p", { class: "text-xl" }, " Your phone system subscription is inactive ", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, " Go to current subscription to get more details ", -1)
const _hoisted_25 = {
  key: 1,
  class: "text-center my-12"
}
const _hoisted_26 = /*#__PURE__*/_createElementVNode("p", { class: "text-xl" }, " You have no phone system subscription ", -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, " To start using phone system purchase the subscription ", -1)

import { useCompanyProfile } from '@/account';
import { usePhoneSystem } from '@/billing/phone-system/use-phone-system';
import {
	SvgIcon,
	UiButton,
	UiCard,
	UiConfirmation,
	UiConfirmationRef,
	UiLine,
	UiLoader,
	useLoading
} from '@/ui-kit';
import { Nullable } from '@/utils';
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import AddPhoneNumberDialog from '../components/AddPhoneNumberDialog.vue';
import {
	useNewPhoneNumbers,
	useSubAccount
} from '../composition';
import {
	useTimeZonesState,
	useSmsTrustState
} from '../state';
import { PurchasedPhone } from '../types';


export default /*#__PURE__*/_defineComponent({
  __name: 'PhoneNumbersRoot',
  setup(__props) {

const router = useRouter();
const toast = useToast();
const { loading: removeLoading, toggleLoading } =
	useLoading();
const { hasPhoneSystem, isPhoneSystemActive } =
	usePhoneSystem();
const smsTrust = useSmsTrustState();

const {
	loadPhoneNumbers,
	isLoading: phoneNumbersLoading,
	phoneNumbers,
	removePhoneNumber
} = useNewPhoneNumbers();
const { isManager, userProfile } = useCompanyProfile();
const {
	loadSubAccount,
	hasSubAccount,
	isLoading: isLoadingSubAccount,
	createSubAccount,
	closeSubAccount
} = useSubAccount();
const { isPaidAccount } = useCompanyProfile();
const { loadTimeZones } = useTimeZonesState();

const dialog =
	ref<
		Nullable<InstanceType<typeof AddPhoneNumberDialog>>
	>(null);
const confirmation: UiConfirmationRef = ref(null);
const disableConfirmation: UiConfirmationRef = ref(null);
const selectedPhoneNumber =
	ref<Nullable<PurchasedPhone>>(null);
const buyMoreConfirmationRef =
	ref<InstanceType<typeof UiConfirmation>>();
const route = useRoute();

const toggleSubAccount = async () => {
	if (!hasSubAccount.value) {
		await createSubAccount();
		await Promise.all([
			loadPhoneNumbers(),
			smsTrust.loadTrust()
		]);
		toast.success(
			'Phone numbers were successfully enabled!'
		);
	} else {
		await closeSubAccount();
		toast.success(
			'Phone numbers were successfully disabled!'
		);
	}
};

const state = usePhoneSystem();

const addPhoneNumber = async () => {
	if (
		isPaidAccount.value &&
		state.totalAvailablePhoneNumbers.value <=
			phoneNumbers.value.length
	) {
		const result =
			await buyMoreConfirmationRef.value?.openDialog(
				'You have reached phone numbers limit.',
				'You have reached phone numbers limit in your subscription. Please, purchase more phone numbers.',
				{
					confirmTheme: 'primary'
				}
			);

		if (result) {
			return router.push({
				name: 'CurrentSubscription'
			});
		}

		return;
	}
	dialog.value?.openDialog();
};

const chargePhoneNumber = (number: any) => {
	// chargePhoneNumberRequest(number.phoneNumber);
};

const disablePhoneNumbers = () => {
	disableConfirmation.value?.openDialog(
		'Are you sure you want to disable telephony?',
		"Disabling telephon will remove all phone numbers, settings and call records. This action <b>can't be undone</b>. Are you sure you want to procceed?"
	);
};

const triggerRemovePhoneNumber = async () => {
	if (
		!userProfile.value?.canRemovePhoneNumbers ||
		!selectedPhoneNumber.value?.canBeRemoved
	) {
		return toast.warning(
			'To prevent fraud we are deleting phone numbers only by requests'
		);
	}

	if (!selectedPhoneNumber.value) {
		return;
	}

	toggleLoading(true);
	await removePhoneNumber(selectedPhoneNumber.value.id);
	confirmation.value?.closeDialog();
	selectedPhoneNumber.value = null;
	toast.success('Phone number was removed');
	toggleLoading(false);
};

const triggerDisableTelephony = async () => {
	await toggleSubAccount();
	disableConfirmation.value?.closeDialog();
};

loadTimeZones();

const { loading: syncing, toggleLoading: toggleSyncing } =
	useLoading(false);

onBeforeMount(async () => {
	if (!hasSubAccount.value) {
		await loadSubAccount();
	}

	if (hasSubAccount.value) {
		toggleSyncing();
		await Promise.all([
			loadPhoneNumbers(),
			smsTrust.loadTrust()
		]);
		toggleSyncing();
	}

	if (route.params.openCreate && hasSubAccount.value) {
		addPhoneNumber();
	}
});

function triggerDeletePhoneNumber(
	phoneNumber: PurchasedPhone
) {
	selectedPhoneNumber.value = phoneNumber;
	confirmation.value?.openDialog(
		`Delete ${phoneNumber.friendlyName}?`,
		'Are you sure you want delete this phone number. You won`t be able to use it for sending sms and making calls.'
	);
}

function toPurchase() {
	return router.push({ name: 'AvailablePlans' });
}

function toSubscription() {
	return router.push({ name: 'CurrentSubscription' });
}

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _directive_section_focus = _resolveDirective("section-focus")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives((_openBlock(), _createBlock(_unref(UiCard), {
      theme: "white",
      class: "relative pt-4 px-4 flex-grow"
    }, {
      default: _withCtx(() => [
        (_unref(hasPhoneSystem))
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (_unref(isPhoneSystemActive))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["flex gap-4 items-center justify-between p-2 border rounded-md mb-3 transition", {
						'border-gray': _unref(smsTrust).isLoading,
						'border-blue-400':
							!_unref(smsTrust).isLoading &&
							!_unref(smsTrust).trustState,
						'border-red-400':
							!_unref(smsTrust).isLoading &&
							_unref(smsTrust).trustState &&
							_unref(smsTrust).isNonComplient,
						'border-green-400':
							!_unref(smsTrust).isLoading &&
							_unref(smsTrust).trustState &&
							_unref(smsTrust).isSuccess,
						'border-yellow-500':
							!_unref(smsTrust).isLoading &&
							_unref(smsTrust).trustState &&
							_unref(smsTrust).isPending
					}])
                    }, [
                      (_unref(smsTrust).isLoading)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_1))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createElementVNode("div", _hoisted_2, [
                              (
									!_unref(smsTrust).trustState
										?.data
								)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_3, " Activate US text messaging "))
                                : (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_unref(smsTrust).isNonComplient
										? `US text messaging verification failed`
										: _unref(smsTrust).isSuccess
										? `US text messaging verification success`
										: `US text messaging verification submitted ( Status: pending )`), 1)),
                              _hoisted_5
                            ]),
                            _createElementVNode("div", null, [
                              _createVNode(_component_RouterLink, { to: "/app/settings/trust" }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(UiButton), {
                                    theme: "primary",
                                    class: "!h-10"
                                  }, {
                                    default: _withCtx(() => [
                                      (
											!_unref(smsTrust)
												.trustState
												?.data
										)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            _createTextVNode(" Get Started ")
                                          ], 64))
                                        : (
											_unref(smsTrust).isNonComplient
										)
                                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                              _createTextVNode(" View and fix errors ")
                                            ], 64))
                                          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                              _createTextVNode(" View Details ")
                                            ], 64))
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ])
                          ], 64))
                    ], 2),
                    _createElementVNode("div", _hoisted_6, [
                      _hoisted_7,
                      (_unref(hasSubAccount))
                        ? (_openBlock(), _createBlock(_unref(UiButton), {
                            key: 0,
                            tabindex: "0",
                            class: "!text-sm !h-10 !px-2",
                            theme: "primary",
                            loading: _unref(phoneNumbersLoading),
                            disabled: _unref(isLoadingSubAccount),
                            onClick: addPhoneNumber
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SvgIcon), {
                                class: "h-5 w-5 mr-2",
                                name: "only-plus"
                              }),
                              _createTextVNode("Add Phone Number ")
                            ]),
                            _: 1
                          }, 8, ["loading", "disabled"]))
                        : _createCommentVNode("", true)
                    ]),
                    _createVNode(_unref(UiLine)),
                    (_unref(isLoadingSubAccount))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                          _createVNode(_unref(UiLoader))
                        ]))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          (_unref(hasSubAccount))
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                (!_unref(phoneNumbersLoading))
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      (_unref(phoneNumbers).length)
                                        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_unref(phoneNumbers), (phoneNumber) => {
                                            return (_openBlock(), _createBlock(_component_RouterLink, {
                                              tabindex: "0",
                                              key: phoneNumber.id,
                                              class: "group flex justify-between items-center mb-1.5 cursor-pointer hover:bg-dark-blue-400 hover:bg-opacity-10 py-1 px-2 rounded-md transition",
                                              to: {
										name: 'PhoneNumberDetails',
										params: {
											id: phoneNumber.id
										}
									}
                                            }, {
                                              default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_9, [
                                                  _createElementVNode("span", null, _toDisplayString(phoneNumber.friendlyName), 1),
                                                  _createElementVNode("span", _hoisted_10, _toDisplayString(phoneNumber.phoneNumber), 1)
                                                ]),
                                                _createElementVNode("div", _hoisted_11, [
                                                  _createVNode(_component_el_tooltip, {
                                                    "show-after": 
												500
											,
                                                    "hide-after": 0,
                                                    enterable: 
												false
											,
                                                    content: "Remove phone number"
                                                  }, {
                                                    default: _withCtx(() => [
                                                      _createVNode(_unref(SvgIcon), {
                                                        tabindex: "0",
                                                        onClick: _withModifiers(($event: any) => (
													triggerDeletePhoneNumber(
														phoneNumber
													)
												), ["stop","prevent"]),
                                                        class: "h-5 w-5 outline-none",
                                                        name: "close"
                                                      }, null, 8, ["onClick"])
                                                    ]),
                                                    _: 2
                                                  }, 1024)
                                                ])
                                              ]),
                                              _: 2
                                            }, 1032, ["to"]))
                                          }), 128))
                                        : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                            _hoisted_13,
                                            _hoisted_14,
                                            (!_unref(isManager))
                                              ? (_openBlock(), _createBlock(_unref(UiButton), {
                                                  key: 0,
                                                  class: "mt-6",
                                                  icon: "plus",
                                                  loading: 
										_unref(phoneNumbersLoading)
									,
                                                  disabled: 
										!_unref(hasSubAccount) ||
										_unref(phoneNumbersLoading)
									,
                                                  onClick: addPhoneNumber
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createTextVNode("Add Phone Number")
                                                  ]),
                                                  _: 1
                                                }, 8, ["loading", "disabled"]))
                                              : _createCommentVNode("", true)
                                          ]))
                                    ], 64))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
                                        return _createElementVNode("div", {
                                          key: n,
                                          class: "flex flex-col mb-3"
                                        }, _hoisted_18)
                                      }), 64))
                                    ]))
                              ], 64))
                            : (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                _hoisted_20,
                                (!_unref(isManager))
                                  ? (_openBlock(), _createBlock(_unref(UiButton), {
                                      key: 0,
                                      class: "mt-6",
                                      theme: "primary-outlined",
                                      loading: _unref(isLoadingSubAccount),
                                      onClick: toggleSubAccount
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode("Enable voice and sms")
                                      ]),
                                      _: 1
                                    }, 8, ["loading"]))
                                  : _createCommentVNode("", true)
                              ]))
                        ], 64)),
                    _createVNode(_Transition, {
                      "enter-active-class": "transition duration-100 ease-out",
                      "enter-from-class": "transform scale-95 opacity-0",
                      "enter-to-class": "transform scale-100 opacity-100",
                      "leave-active-class": "transition duration-75 ease-in",
                      "leave-from-class": "transform scale-100 opacity-100",
                      "leave-to-class": "transform scale-95 opacity-0"
                    }, {
                      default: _withCtx(() => [
                        (_unref(syncing))
                          ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                              _createTextVNode(" Syncing phone numbers "),
                              _createVNode(_unref(UiLoader), { class: "ml-2" })
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ], 64))
                : (_openBlock(), _createElementBlock("div", _hoisted_22, [
                    _hoisted_23,
                    _hoisted_24,
                    _createVNode(_unref(UiButton), {
                      theme: "primary-outlined",
                      onClick: toSubscription,
                      class: "mt-6"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(SvgIcon), {
                          class: "mr-2 h-5 w-5",
                          name: "external-link"
                        }),
                        _createTextVNode(" To Subscription ")
                      ]),
                      _: 1
                    })
                  ]))
            ], 64))
          : (_openBlock(), _createElementBlock("div", _hoisted_25, [
              _hoisted_26,
              _hoisted_27,
              _createVNode(_unref(UiButton), {
                theme: "primary-outlined",
                onClick: toPurchase,
                class: "mt-6"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(SvgIcon), {
                    class: "mr-2 h-5 w-5",
                    name: "external-link"
                  }),
                  _createTextVNode(" To Purchase ")
                ]),
                _: 1
              })
            ]))
      ]),
      _: 1
    })), [
      [_directive_section_focus]
    ]),
    _createVNode(AddPhoneNumberDialog, {
      ref_key: "dialog",
      ref: dialog
    }, null, 512),
    _createVNode(_unref(UiConfirmation), {
      ref_key: "confirmation",
      ref: confirmation,
      loading: _unref(removeLoading),
      onConfirm: triggerRemovePhoneNumber
    }, null, 8, ["loading"]),
    _createVNode(_unref(UiConfirmation), {
      ref_key: "disableConfirmation",
      ref: disableConfirmation,
      onConfirm: triggerDisableTelephony
    }, null, 512),
    _createVNode(_unref(UiConfirmation), {
      ref_key: "buyMoreConfirmationRef",
      ref: buyMoreConfirmationRef,
      confirmation: "Buy more"
    }, null, 512)
  ], 64))
}
}

})